import React, { useState, useEffect, useRef, lazy, Suspense, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Keyboard } from '@capacitor/keyboard';
import { Share } from '@capacitor/share';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import Cast from './cast-plugin';
import { Airplay, Bell, BellOff, Calendar, MessageCircle, Clock, Heart, Home, Menu, Music, Plus, Settings, Share2, X, Play, Pause, Volume2, VolumeX, ImageOff, Facebook, Instagram, BarChart3, Star, ChevronLeft, ChevronRight, ArrowUpSquare, Radio } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import logo from './logo.png';
import mixcloudIcon from './assets/mixcloud.svg';
import { useNotifications, subscribeUserToPush } from './hooks/useNotifications';
import { useListeningStats } from './hooks/useListeningStats';
import { useCast } from './hooks/useCast';

import { Routes, Route, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, collection, query, orderBy, where } from 'firebase/firestore';
import { db, auth } from './firebase';
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import scheduleData from './scheduleData';
import SleepTimer from './components/SleepTimer';
import ListeningStats from './components/ListeningStats';
import NotificationSettings from './components/NotificationSettings';
import SchedulePage from './components/SchedulePage';
import DarkVeil from './components/DarkVeil';
import {
  trackStreamStart,
  trackStreamStop,
  trackStreamError,
  trackVolumeChange,
  trackCastingStart,
  trackCastingStop,
  trackChatOpen,
  trackChatClose,
  trackScheduleView,
  trackSleepTimerStart,
  trackSleepTimerCancel,
  trackSleepTimerComplete,
  trackInstallPromptShown,
  trackInstallPromptAccepted,
  trackInstallPromptDismissed,
  trackPageView,
  trackMenuOpen,
  trackMenuClose,
  trackSocialMediaClick,
  trackShareClick,
  trackListeningStatsView,
  trackError,
  trackUserEngagement,
  trackConnectionStatusChange
} from './analytics';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface BluetoothDevice {
  id: string;
  name: string;
}

interface ShowSlot {
  artist: string;
  show?: string;
  time: string;
  image?: string;
}



interface WakeLockSentinel {
  released: boolean;
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
  release(): Promise<void>;
}

declare global {
  interface Window {
    chrome: any;
    __onGCastApiAvailable?: (isAvailable: boolean) => void;
    webkitAudioContext?: typeof AudioContext;
    deferredPrompt?: BeforeInstallPromptEvent;
    MSStream?: any;
  }
  
  interface Navigator {
    standalone?: boolean;
    connection?: {
      effectiveType: string;
      downlink: number;
      rtt: number;
    };
  }
}

// Stream URLs for different qualities
const STREAM_URLS = {
  standard: 'https://stream.playfmdublin.com/radio192.mp3',
  high: 'https://stream.playfmdublin.com/radio320.mp3'
} as const;

type AudioQuality = 'standard' | 'high';

const RECONNECT_BACKOFF_FACTOR = 1.5;

// Polyfill for atob function to ensure compatibility on Android
if (typeof window !== 'undefined') {
  // Store original atob function
  const originalAtob = window.atob;
  
  // Create a safe atob wrapper
  window.atob = function(str: string): string {
    try {
      // Try the original atob first
      if (originalAtob) {
        return originalAtob(str);
      }
    } catch (error) {
      console.warn('Original atob failed, using polyfill:', error);
    }
    
    // Fallback polyfill implementation
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
    let output = '';
    str = String(str).replace(/=+$/, '');
    
    // Validate input
    if (str.length % 4 === 1) {
      throw new Error('Invalid base64 string: The string to be decoded is not correctly encoded');
    }
    
    for (let bc = 0, bs = 0, buffer, i = 0; buffer = str.charAt(i++);) {
      const charIndex = chars.indexOf(buffer);
      if (charIndex === -1) {
        throw new Error('Invalid base64 string: The string to be decoded is not correctly encoded');
      }
      bs = bc % 4 ? bs * 64 + charIndex : charIndex;
      if (bc++ % 4) output += String.fromCharCode(255 & bs >> (-2 * bc & 6));
    }
    return output;
  };
}

const styles = `
  .now-playing-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .now-playing-title {
    font-size: 1rem;
    font-weight: 500;
    color: white;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .now-playing-artist {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.8);
  }

  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }

  .pt-safe {
    padding-top: env(safe-area-inset-top);
  }

  .scrolling-text-container {
    position: relative;
    overflow: hidden;
    width: 100%;
    mask-image: linear-gradient(to right, black 80%, transparent 100%);
  }

  .scrolling-text {
    display: inline-block;
    white-space: nowrap;
    padding-right: 100px;
    animation: scrollText 20s linear infinite;
    will-change: transform;
  }

  .scrolling-text-container:hover .scrolling-text {
    animation-play-state: paused;
  }

  @keyframes scrollText {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-100%);
    }
  }

  .truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// Lazy load components for better performance
const ChatRoom = lazy(() => import('./ChatRoom'));



function App() {
  console.log('🎬 APP FUNCTION START - Component is executing');
  
  // Immediately disable Cast API on iOS to prevent any callbacks
  useEffect(() => {
    if (Capacitor.getPlatform() === 'ios') {
      console.log('🎬 iOS: Disabling Cast API globally at app start');
      // Override the global Cast API callback to prevent any notifications
      window['__onGCastApiAvailable'] = () => {
        console.log('🎬 iOS: Cast API callback blocked');
      };
      // Remove any existing Cast SDK scripts
      const scripts = document.querySelectorAll('script[src*="cast_sender.js"]');
      scripts.forEach(script => script.remove());
    }
  }, []);
  
  // Initialize Firebase
  useEffect(() => {
    try {
      console.log('Firebase initialized in App component');
    } catch (error) {
      console.error('Error initializing Firebase in App component:', error);
    }
  }, []);

  // UI State
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [currentTrack, setCurrentTrack] = useState({
    title: 'Play FM Dublin',
    artist: 'Live Radio',
    artwork: logo,
    album: '24/7 From Ireland With Love'
  });
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [isAirPlayAvailable, setIsAirPlayAvailable] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isBluetoothConnected, setIsBluetoothConnected] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showChat, setShowChat] = useState(false);

  const [unreadMessages, setUnreadMessages] = useState(0);
  const [browserType, setBrowserType] = useState<string>('');
  const [showSleepTimer, setShowSleepTimer] = useState(false);
  const [showListeningStats, setShowListeningStats] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [currentQuality, setCurrentQuality] = useState<AudioQuality>(() => {
    const saved = localStorage.getItem('playfm_audio_quality');
    return (saved === 'high' || saved === 'standard') ? saved : 'standard';
  });
  const [selectedDay, setSelectedDay] = useState('today');
  const [currentShow, setCurrentShow] = useState<any>(null);
  const [nextShow, setNextShow] = useState<any>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [schedule, setSchedule] = useState<any>(null);
  const [lastUpdate, setLastUpdate] = useState<number>(Date.now());
  const [error, setError] = useState<string | null>(null);
  const [brokenImages, setBrokenImages] = useState<{ [url: string]: boolean }>({});
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [lastSeenMessageTime, setLastSeenMessageTime] = useState<number>(() => {
    const saved = localStorage.getItem('lastSeenMessageTime');
    return saved ? parseInt(saved) : Date.now();
  });

  // Initialize Firebase
  useEffect(() => {
    try {
      console.log('Firebase initialized in App component');
    } catch (error) {
      console.error('Error initializing Firebase in App component:', error);
    }
  }, []);


  // Audio State - Simplified and robust
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  // Refs
  const audioRef = useRef<HTMLAudioElement>(null);
  const swiperRef = useRef<SwiperType>();
  const lastMessageTimestampRef = useRef<Date | null>(null);
  const metadataIntervalRef = useRef<number>();
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaSessionInitializedRef = useRef(false);
  
  const METADATA_UPDATE_INTERVAL = 30000; // Combined metadata and current show updates (active)
  const BACKGROUND_UPDATE_INTERVAL = 300000; // 5 minutes for background updates

  // Hooks for new features
  const { startSession, endSession, formatDuration, getCurrentSessionDuration } = useListeningStats();
  const { scheduleShowReminder, toggleFavoriteShow, isFavoriteShow } = useNotifications();
  const { castState, initializeCast, startCastSession, stopCastSession, loadMedia: loadCastMedia, clearError } = useCast();
  
  // Version checking and update notifications


  const navigate = useNavigate();

  // Add authReady state
  const [authReady, setAuthReady] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  // Add missing casting state variables
  const [isCasting, setIsCasting] = useState(false);
  const [castSession, setCastSession] = useState<any>(null);

  useEffect(() => {
    setAuthLoading(true);
    const unsubscribe = onAuthStateChanged(auth, user => {
      if (!user) {
        signInAnonymously(auth).then(() => setAuthReady(true)).finally(() => setAuthLoading(false));
      } else {
        setAuthReady(true);
        setAuthLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  // Listen for new chat messages to update unread count
  useEffect(() => {
    if (!authReady) return;

    const messagesRef = collection(db, 'messages');
    const messagesQuery = query(messagesRef, orderBy('timestamp', 'desc'));

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      if (!showChat) {
        let newUnreadCount = 0;
        snapshot.docs.forEach((doc) => {
          const message = doc.data();
          if (message.timestamp && message.timestamp.toMillis() > lastSeenMessageTime) {
            newUnreadCount++;
          }
        });
        setUnreadMessages(newUnreadCount);
      }
    });

    return unsubscribe;
  }, [authReady, lastSeenMessageTime, showChat]);

  // Track audio state changes for detection
  const [lastPlayTime, setLastPlayTime] = useState<number>(0);
  const [lastPauseTime, setLastPauseTime] = useState<number>(0);
  
  // User activity detection for smart timer management
  const [lastUserActivity, setLastUserActivity] = useState<number>(Date.now());
  const [isUserActive, setIsUserActive] = useState<boolean>(true);
  const [playAttempts, setPlayAttempts] = useState<number>(0);
  const [consecutiveFailures, setConsecutiveFailures] = useState<number>(0);

  // Function declarations
  const handleChatOpen = () => {
    trackChatOpen();
    setUnreadMessages(0);
    const now = Date.now();
    setLastSeenMessageTime(now);
    localStorage.setItem('lastSeenMessageTime', now.toString());
    setShowChat(true);
  };

  const handleShare = async () => {
    trackShareClick();
    // Create personalized share message based on current show
    let shareMessage = `Checkout the new PlayFm Dublin app and tunein live! 🎵`;
    
    if (currentShow && currentShow.artist) {
      if (currentShow.show) {
        shareMessage = `I'm listening to ${currentShow.artist} - ${currentShow.show} on the new PlayFm Dublin app! 🎵`;
      } else {
        shareMessage = `I'm listening to ${currentShow.artist} on the new PlayFm Dublin app! 🎵`;
      }
    }

    try {
      await Share.share({
        title: 'PlayFm Dublin',
        text: shareMessage,
        url: 'https://playfmdublin.app',
        dialogTitle: 'Share PlayFm Dublin',
      });
    } catch (err) {
      console.error('Share failed:', err);
      // Fallback for browsers that might not fully support Capacitor's implementation
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'PlayFm Dublin',
            text: shareMessage,
            url: 'https://playfmdublin.app'
          });
        } catch (webShareErr) {
          console.error('Web Share failed:', webShareErr);
        }
      }
    }
  };

  // Define the mainIcons array after function declarations
  const mainIcons = [
    {
      label: 'Chat',
      icon: MessageCircle,
      onClick: handleChatOpen,
      aria: 'Chat',
      badge: unreadMessages > 0 ? unreadMessages : null,
    },
    {
      label: 'Schedule',
      icon: Calendar,
      onClick: () => {
        trackScheduleView();
        navigate('/schedule');
      },
      aria: 'Schedule',
    },
    {
      label: 'Timer',
      icon: Clock,
      onClick: () => setShowSleepTimer(true),
      aria: 'Sleep Timer',
    },
    {
      label: 'Mixcloud',
      icon: ({ className = '' }) => (
        <img 
          src={mixcloudIcon} 
          alt="Mixcloud" 
          className={`w-6 h-6 inline-block align-bottom [filter:brightness(0)_invert(1)] group-hover:[filter:brightness(0)_invert(0.5)_sepia(1)_saturate(1000)_hue-rotate(300deg)] -mb-1 ${className}`}
        />
      ),
      onClick: () => {
        trackSocialMediaClick('mixcloud');
        window.open('https://mixcloud.com/playfmdublin', '_blank');
      },
      aria: 'Mixcloud',
      isCustom: true,
    },
    {
      label: 'Facebook',
      icon: Facebook,
      onClick: () => {
        trackSocialMediaClick('facebook');
        window.open('https://facebook.com/playfmdublin', '_blank');
      },
      aria: 'Facebook',
    },
    {
      label: 'Instagram',
      icon: Instagram,
      onClick: () => {
        trackSocialMediaClick('instagram');
        window.open('https://instagram.com/playfmdublin', '_blank');
      },
      aria: 'Instagram',
    }
  ];

  // Debug initial state (moved to useEffect to prevent excessive logging)
  useEffect(() => {
    console.log('🔍 Initial state values:');
    console.log('🔍 isAirPlayAvailable:', isAirPlayAvailable);
    console.log('🔍 castState.isAvailable:', castState.isAvailable);
    console.log('🔍 Button disabled:', !isAirPlayAvailable && !castState.isAvailable);
  }, [isAirPlayAvailable, castState.isAvailable]);

  // === ROBUST AUDIO SYSTEM ===

  // Initialize audio context for mobile devices
  const initializeAudioContext = () => {
    if (!audioContextRef.current) {
      try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass) {
          audioContextRef.current = new AudioContextClass();
          console.log('Audio context initialized');
        }
      } catch (error) {
        console.error('Failed to initialize audio context:', error);
      }
    }
  };

  // Resume audio context if suspended (required for mobile)
  const resumeAudioContext = async () => {
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
        console.log('Audio context resumed');
      } catch (error) {
        console.error('Failed to resume audio context:', error);
      }
    }
  };

  // Robust play/pause function
  const handlePlayPause = async () => {
    if (castState.isConnected && castState.currentSession && castState.currentSession.media && castState.currentSession.media.length > 0) {
       // logic to play/pause on chromecast
      const media = castState.currentSession.media[0];
      if (media.playerState === window.chrome.cast.media.PlayerState.PLAYING) {
        media.pause(null, 
          () => console.log('Media paused'), 
          (error: chrome.cast.Error) => console.error('Error pausing media:', error)
        );
      } else {
        media.play(null, 
          () => console.log('Media playing'), 
          (error: chrome.cast.Error) => console.error('Error playing media:', error)
        );
      }
      return;
    }
    if (!audioRef.current) return;
    const audio = audioRef.current;
    try {
      await resumeAudioContext();
      if (audio.paused || audio.ended) {
        setIsLoading(true);
        audio.load(); // Always reload before play
        if (Capacitor.getPlatform() === 'ios') {
          if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
          }
        }
        await audio.play();
        startSession();
      } else {
        audio.pause();
        endSession();
      }
    } catch (error) {
      setIsLoading(false);
      setStreamError(true);
      setIsPlaying(false);
    }
  };

  // Get current playing state
  const getIsPlaying = () => {
    if (castState.isConnected) return true;
    return isPlaying;
  };

  // Initialize Media Session API only once
  const initializeMediaSession = () => {
    if (!mediaSessionInitializedRef.current && 'mediaSession' in navigator) {
      try {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: currentTrack.title,
          artist: currentTrack.artist,
          artwork: [
            { src: '/logo.png', sizes: '96x96', type: 'image/png' },
            { src: '/logo.png', sizes: '128x128', type: 'image/png' },
            { src: '/logo.png', sizes: '192x192', type: 'image/png' },
            { src: '/logo.png', sizes: '256x256', type: 'image/png' },
            { src: '/logo.png', sizes: '384x384', type: 'image/png' },
            { src: '/logo.png', sizes: '512x512', type: 'image/png' }
          ]
        });
        navigator.mediaSession.setActionHandler('play', async () => {
          if (audioRef.current) {
            try {
              await resumeAudioContext();
              if (audioRef.current.paused || audioRef.current.ended) {
                audioRef.current.load(); // Always reload before play
                if (Capacitor.getPlatform() === 'ios') {
                  if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                    await audioContextRef.current.resume();
                  }
                }
                await audioRef.current.play();
                startSession();
              }
            } catch (error) {
              // Silent fail
            }
          }
        });
        navigator.mediaSession.setActionHandler('pause', async () => {
          if (audioRef.current && !audioRef.current.paused) {
            audioRef.current.pause();
            endSession();
          }
        });
        navigator.mediaSession.setActionHandler('stop', async () => {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current.load();
            endSession();
          }
        });
        navigator.mediaSession.setActionHandler('seekto', (details) => {
          // No-op for live radio
        });
        mediaSessionInitializedRef.current = true;
      } catch (error) {
        // Silent fail
      }
    }
  };

  // Volume and mute controls
  const handleVolumeChange = (newVolume: number) => {
    trackVolumeChange(isMuted, newVolume);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : newVolume;
    }
  };

  const handleMuteToggle = () => {
    const newMuted = !isMuted;
    trackVolumeChange(newMuted, volume);
    setIsMuted(newMuted);
    if (audioRef.current) {
      audioRef.current.muted = newMuted;
      audioRef.current.volume = newMuted ? 0 : volume;
    }
  };

  const toggleMute = () => {
    handleMuteToggle();
  };

  // Initialize audio element once
  useEffect(() => {
    if (audioRef.current) {
      console.log('Setting up audio element...');
      
      const audio = audioRef.current;
      
      // Basic setup with mobile optimizations
      audio.preload = 'auto';
      audio.crossOrigin = 'anonymous';
      audio.src = STREAM_URLS[currentQuality];
      audio.volume = isMuted ? 0 : volume;
      audio.muted = isMuted;
      
      // Mobile-specific optimizations
      audio.autoplay = false;
      audio.controls = false;
      
      // Streaming optimizations
      audio.loop = false;
      audio.defaultMuted = false;
      
      // Background audio optimizations
      audio.preservesPitch = false; // Better for streaming
      if ('webkitPreservesPitch' in audio) {
        (audio as any).webkitPreservesPitch = false;
      }
      
      // Ensure proper buffering for streaming
      audio.buffered;
      
      // Add essential event listeners for state management
      const handlePlay = () => {
        trackStreamStart();
        setIsPlaying(true);
        setIsLoading(false);
        setStreamError(false);
        setLastPlayTime(Date.now());
        setPlayAttempts(prev => prev + 1);
        setConsecutiveFailures(0); // Reset failures on successful play
        syncMediaSessionState();
      };

      const handlePause = () => {
        trackStreamStop();
        setIsPlaying(false);
        setIsLoading(false);
        setLastPauseTime(Date.now());
        syncMediaSessionState();
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setIsLoading(false);
        syncMediaSessionState();
      };

      const handleError = (e: Event) => {
        trackStreamError('audio_error');
        setIsPlaying(false);
        setIsLoading(false);
        setStreamError(true);
        setConsecutiveFailures(prev => prev + 1);
        syncMediaSessionState();
        
        // Try to recover from error
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.load();
          }
        }, 1000);
      };

      const handleLoadStart = () => {
        setIsLoading(true);
      };

      const handleCanPlay = () => {
        setIsLoading(false);
      };

      const handleStalled = () => {
        setIsLoading(true);
        
        // Try to recover from stall
        setTimeout(() => {
          if (audioRef.current && isPlaying) {
            audioRef.current.load();
          }
        }, 2000);
      };

      const handleWaiting = () => {
        setIsLoading(true);
      };

      const handlePlaying = () => {
        setIsLoading(false);
      };

      const handleAbort = () => {
        setIsPlaying(false);
        setIsLoading(false);
        syncMediaSessionState();
      };

      const handleSuspend = () => {
        // Don't change playing state on suspend, just loading
        setIsLoading(false);
      };

      const handleEmptied = () => {
        // Audio element was reset, sync state
        setIsPlaying(false);
        setIsLoading(false);
        syncMediaSessionState();
      };

      // Add event listeners
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('stalled', handleStalled);
      audio.addEventListener('waiting', handleWaiting);
      audio.addEventListener('playing', handlePlaying);
      audio.addEventListener('abort', handleAbort);
      audio.addEventListener('suspend', handleSuspend);
      audio.addEventListener('emptied', handleEmptied);

      // Load the audio
      audio.load();

      // Initialize audio context and media session
      initializeAudioContext();
      initializeMediaSession();

      return () => {
        console.log('Cleaning up audio element...');
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
        audio.removeEventListener('loadstart', handleLoadStart);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('stalled', handleStalled);
        audio.removeEventListener('waiting', handleWaiting);
        audio.removeEventListener('playing', handlePlaying);
        audio.removeEventListener('abort', handleAbort);
        audio.removeEventListener('suspend', handleSuspend);
        audio.removeEventListener('emptied', handleEmptied);
        audio.pause();
        audio.src = '';
      };
    }
  }, [currentQuality]); // Re-run when quality changes

  // Update media session metadata when current track changes
  useEffect(() => {
    if ('mediaSession' in navigator && mediaSessionInitializedRef.current) {
      try {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: currentTrack.title,
          artist: currentTrack.artist,
          album: currentTrack.album || 'PlayFm Dublin',
          artwork: [
            { src: currentTrack.artwork || '/logo.png', sizes: '96x96', type: 'image/png' },
            { src: currentTrack.artwork || '/logo.png', sizes: '128x128', type: 'image/png' },
            { src: currentTrack.artwork || '/logo.png', sizes: '192x192', type: 'image/png' },
            { src: currentTrack.artwork || '/logo.png', sizes: '256x256', type: 'image/png' },
            { src: currentTrack.artwork || '/logo.png', sizes: '384x384', type: 'image/png' },
            { src: currentTrack.artwork || '/logo.png', sizes: '512x512', type: 'image/png' }
          ]
        });
      } catch (error) {
        console.error('Failed to update media session metadata:', error);
      }
    }
  }, [currentTrack.title, currentTrack.artist, currentTrack.album, currentTrack.artwork]);

  // Handle online/offline
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setStreamError(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      if (audioRef.current && isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        setStreamError(true);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isPlaying]);

  // Combined metadata and current show updates with adaptive polling and user activity detection
  useEffect(() => {
    const checkAndUpdateAll = () => {
      // Skip updates if user has been inactive for more than 2 minutes, BUT only if audio is not playing
      const timeSinceLastActivity = Date.now() - lastUserActivity;
      if (timeSinceLastActivity > 2 * 60 * 1000 && !isPlaying) {
        console.log('User inactive for 2+ minutes and audio not playing, skipping metadata update');
        return;
      }
      
      // Always update metadata to ensure automatic updates during playback
      updateMetadata();
      updateCurrentShow(); // Always update current show regardless of playing state
    };

    // Run initial updates when starting to play or when schedule changes
    // Always update metadata immediately
    updateMetadata();
    updateCurrentShow(); // Always update current show
    
    // Use adaptive polling interval based on visibility and user activity
    let interval = document.hidden ? BACKGROUND_UPDATE_INTERVAL : METADATA_UPDATE_INTERVAL;
    
    // Extend interval if user is inactive
    const timeSinceLastActivity = Date.now() - lastUserActivity;
    if (timeSinceLastActivity > 5 * 60 * 1000) { // 5 minutes
      interval = Math.max(interval, 60000); // At least 1 minute when inactive
    }
    
    metadataIntervalRef.current = window.setInterval(checkAndUpdateAll, interval);

    return () => {
      if (metadataIntervalRef.current) {
        clearInterval(metadataIntervalRef.current);
      }
    };
  }, [isPlaying, schedule, lastUserActivity]); // Run when playing state changes or schedule changes

  // User activity detection and smart timer management
  useEffect(() => {
    const updateUserActivity = () => {
      const now = Date.now();
      setLastUserActivity(now);
      setIsUserActive(true);
    };

    const checkUserActivity = () => {
      const timeSinceLastActivity = Date.now() - lastUserActivity;
      const wasActive = isUserActive;
      const nowActive = timeSinceLastActivity < 5 * 60 * 1000; // 5 minutes
      
      if (wasActive !== nowActive) {
        setIsUserActive(nowActive);
        console.log(`User activity changed: ${nowActive ? 'active' : 'inactive'}`);
      }
    };

    // Add event listeners for user activity detection
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      document.addEventListener(event, updateUserActivity, { passive: true });
    });

    // Check user activity every minute
    const activityCheckInterval = setInterval(checkUserActivity, 60000);

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateUserActivity);
      });
      clearInterval(activityCheckInterval);
    };
  }, [lastUserActivity, isUserActive]);

  // Add visibility API support with adaptive polling
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('App hidden - switching to 5-minute polling and keeping audio playing');
        // Switch to background polling interval
        if (metadataIntervalRef.current) {
          clearInterval(metadataIntervalRef.current);
          const checkAndUpdateAll = () => {
            // Skip updates if user has been inactive for more than 2 minutes, BUT only if audio is not playing
            const timeSinceLastActivity = Date.now() - lastUserActivity;
            if (timeSinceLastActivity > 2 * 60 * 1000 && !isPlaying) {
              console.log('User inactive for 2+ minutes and audio not playing, skipping background update');
              return;
            }
            
            // Always update metadata to ensure automatic updates during playback
            updateMetadata();
            updateCurrentShow();
          };
          metadataIntervalRef.current = window.setInterval(checkAndUpdateAll, BACKGROUND_UPDATE_INTERVAL);
        }
        // Ensure audio continues playing in background
        if (audioRef.current && isPlaying && audioRef.current.paused) {
          console.log('Audio was paused unexpectedly, attempting to resume...');
          audioRef.current.play().catch(error => {
            console.warn('Could not resume audio in background:', error);
          });
        }
      } else {
        console.log('App visible - switching to 30-second polling and updating immediately');
        // Reset user activity timestamp when app becomes visible
        setLastUserActivity(Date.now());
        
        // Immediately update when app becomes visible
        updateCurrentShow();
        updateMetadata();
        
        // Switch back to active polling interval
        if (metadataIntervalRef.current) {
          clearInterval(metadataIntervalRef.current);
          const checkAndUpdateAll = () => {
            // Skip updates if user has been inactive for more than 2 minutes, BUT only if audio is not playing
            const timeSinceLastActivity = Date.now() - lastUserActivity;
            if (timeSinceLastActivity > 2 * 60 * 1000 && !isPlaying) {
              console.log('User inactive for 2+ minutes and audio not playing, skipping metadata update');
              return;
            }
            
            // Always update metadata to ensure automatic updates during playback
            updateMetadata();
            updateCurrentShow();
          };
          metadataIntervalRef.current = window.setInterval(checkAndUpdateAll, METADATA_UPDATE_INTERVAL);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPlaying, schedule, lastUserActivity]);

  // ===== REST OF THE COMPONENT (unchanged) =====

  useEffect(() => {
    const detectBrowser = () => {
      const ua = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(ua);
      
      if (!isIOS) return;

      if (ua.includes('CriOS')) {
        setBrowserType('chrome');
      } else if (ua.includes('FxiOS')) {
        setBrowserType('firefox');
      } else if (ua.includes('Safari')) {
        setBrowserType('safari');
      }
    };

    detectBrowser();
  }, []);

  const getInstallInstructions = () => {
    switch (browserType) {
      case 'safari':
        return 'To install this app:\\n\\n1. Tap the Share button on Safari\\n2. Scroll down and tap "Add to Home Screen"\\n3. Tap "Add" in the top right corner';
      case 'chrome':
        return 'To install this app:\\n\\n1. Tap the three dots menu (⋮) at the bottom of your screen\\n2. Tap "Share..."\\n3. Scroll down and tap "Add to Home Screen"\\n4. Tap "Add" to confirm';
      case 'firefox':
        return 'To install this app:\\n\\n1. Tap the share icon (rectangle with arrow) at the bottom of your screen\\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to confirm';
      default:
        return 'To install this app, add it to your home screen from your browser\'s menu.';
    }
  };

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('[DEBUG] beforeinstallprompt event fired');
      e.preventDefault();
      window.deferredPrompt = e as BeforeInstallPromptEvent;
      console.log('[DEBUG] Deferred prompt stored:', !!window.deferredPrompt);
      setShowInstallPrompt(true);
    };

    const checkInstallState = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isInstalled = isStandalone || 
                         window.navigator.standalone || 
                         document.referrer.includes('android-app://') ||
                         window.location.protocol === 'capacitor-https:';

      if (!isInstalled) {
        setTimeout(() => {
          trackInstallPromptShown();
          setShowInstallPrompt(true);
        }, 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    checkInstallState();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    console.log('[DEBUG] Install click - Device info:', {
      isAndroid,
      isIOS,
      userAgent: navigator.userAgent,
      hasDeferredPrompt: !!window.deferredPrompt,
      standalone: window.navigator.standalone,
      displayMode: window.matchMedia('(display-mode: standalone)').matches
    });
    
    if (isIOS) {
      alert(getInstallInstructions());
    } else if (isAndroid) {
      if (window.deferredPrompt) {
        console.log('[DEBUG] Android: Using deferred prompt');
        try {
          await window.deferredPrompt.prompt();
          const { outcome } = await window.deferredPrompt.userChoice;
          console.log('[DEBUG] Android install outcome:', outcome);
          if (outcome === 'accepted') {
            trackInstallPromptAccepted();
          } else {
            trackInstallPromptDismissed();
          }
          window.deferredPrompt = undefined;
        } catch (error) {
          console.error('[DEBUG] Android install error:', error);
          alert('Installation failed. Please try again or use the browser menu to install.');
        }
      } else {
        console.log('[DEBUG] Android: No deferred prompt, showing manual instructions');
        alert('To install this app on your Android device:\n\n1. Tap the three dots menu (⋮) in your browser\n2. Tap "Install app" or "Add to Home screen"\n3. Follow the installation prompts');
      }
    } else if (window.deferredPrompt) {
      console.log('[DEBUG] Other platform: Using deferred prompt');
      await window.deferredPrompt.prompt();
      const { outcome } = await window.deferredPrompt.userChoice;
      window.deferredPrompt = undefined;
    }
    setShowInstallPrompt(false);
  };



  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    setBrokenImages(prev => ({ ...prev, [target.src]: true }));
  };

  const showToastMessage = (message: string, duration: number = 3000) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
      setToastMessage(null);
    }, duration);
  };

  // Add this useEffect for AirPlay initialization
  useEffect(() => {
    const checkAirPlayAvailability = () => {
      if (audioRef.current && 'webkitShowPlaybackTargetPicker' in audioRef.current) {
        setIsAirPlayAvailable(true);
        console.log('✅ AirPlay available');
      } else {
        setIsAirPlayAvailable(false);
        console.log('❌ AirPlay not available');
      }
    };

    const handleAirPlayChange = (event: any) => {
      console.log('🍎 AirPlay availability changed:', event);
      if(event.availability === 'available') {
        setIsAirPlayAvailable(true);
        console.log('✅ AirPlay became available');
        // Check if we are connected to an AirPlay device
        if (audioRef.current?.webkitCurrentPlaybackTargetIsWireless) {
          setIsCasting(true);
          console.log('✅ Connected to AirPlay device');
        } else {
          setIsCasting(false);
          console.log('❌ Not connected to AirPlay device');
        }
      } else {
        setIsAirPlayAvailable(false);
        setIsCasting(false);
        console.log('❌ AirPlay became unavailable');
      }
    };

    // Check initially after a short delay to ensure audio element is ready
    const checkTimer = setTimeout(() => {
      checkAirPlayAvailability();
    }, 1000);

    // Add event listener for AirPlay availability changes
    const audioEl = audioRef.current;
    if (audioEl) {
      audioEl.addEventListener('webkitplaybacktargetavailabilitychanged', handleAirPlayChange);
    }

    return () => {
      clearTimeout(checkTimer);
      if (audioEl) {
        audioEl.removeEventListener('webkitplaybacktargetavailabilitychanged', handleAirPlayChange);
      }
    };
  }, [audioRef.current]); // Add audioRef.current as dependency

  // Step 9: PWA-optimized Cast initialization with iOS skip
  useEffect(() => {
    const isIOS = Capacitor.getPlatform() === 'ios';
    
    // Skip Cast initialization on iOS - use AirPlay only
    if (isIOS) {
      console.log('🎬 iOS: Skipping Cast initialization, using AirPlay only');
      return;
    }
    
    console.log('🎬 Initializing Cast using new hook system...');
    
    // Step 9.4: Check for persisted Cast session in PWA mode
    const isStandalonePWA = window.matchMedia('(display-mode: standalone)').matches || 
                           window.navigator.standalone;
    
    if (isStandalonePWA) {
      console.log('🎬 PWA: Checking for persisted Cast session...');
      const savedSession = localStorage.getItem('playfm_cast_session');
      if (savedSession) {
        try {
          const sessionData = JSON.parse(savedSession);
          const sessionAge = Date.now() - sessionData.timestamp;
          
          // Only restore session if it's less than 30 minutes old
          if (sessionAge < 30 * 60 * 1000 && sessionData.isConnected) {
            console.log('🎬 PWA: Found recent Cast session, will attempt to restore:', sessionData.receiverName);
            // The actual restoration will happen after Cast API initialization
          } else {
            console.log('🎬 PWA: Saved Cast session is too old, clearing...');
            localStorage.removeItem('playfm_cast_session');
          }
        } catch (error) {
          console.error('🎬 PWA: Error parsing saved Cast session:', error);
          localStorage.removeItem('playfm_cast_session');
        }
      }
    }
    
    initializeCast().catch(error => {
      console.error('🎬 Cast initialization failed:', error);
    });
  }, [initializeCast]);

  // Show Cast errors as toast messages
  useEffect(() => {
    // Never show ANY Cast/Chromecast errors on iOS
    if (castState.error && Capacitor.getPlatform() !== 'ios' && 
        !castState.error.toLowerCase().includes('cast') && 
        !castState.error.toLowerCase().includes('chrome')) {
      setShowToast(true);
      setToastMessage(castState.error);
    }
  }, [castState.error]);



  const debugChromecastDiscovery = () => {
    console.log('🔍 === CHROMECAST DISCOVERY DEBUG ===');
    console.log('🔍 Platform:', Capacitor.getPlatform());
    console.log('🔍 Window.chrome exists:', !!window.chrome);
    console.log('🔍 Window.chrome.cast exists:', !!window.chrome?.cast);
    console.log('🔍 Window.chrome.cast.isAvailable:', !!window.chrome?.cast?.isAvailable);
    console.log('🔍 Network Info:', navigator.connection ? {
      effectiveType: navigator.connection.effectiveType,
      downlink: navigator.connection.downlink,
      rtt: navigator.connection.rtt
    } : 'Not available');
    console.log('🔍 User Agent:', navigator.userAgent);
    console.log('🔍 Current URL:', window.location.href);
    console.log('🔍 Protocol:', window.location.protocol);
    
    // Try to manually trigger device discovery
    if (window.chrome?.cast?.isAvailable) {
      console.log('🔍 Attempting manual device discovery...');
      try {
        window.chrome.cast.requestSession(
          (session: chrome.cast.Session) => {
            console.log('✅ Manual discovery successful!');
            console.log('✅ Found device:', session.receiver.friendlyName);
            console.log('✅ Session ID:', session.sessionId);
            // Close the test session
            session.stop(() => {
              console.log('✅ Test session closed');
            }, (error: chrome.cast.Error) => {
              console.log('⚠️ Error closing test session:', error);
            });
          },
          (error: chrome.cast.Error) => {
            console.log('❌ Manual discovery failed:', error);
            console.log('❌ Error code:', error.code);
            console.log('❌ Error description:', error.description);
          }
        );
      } catch (error) {
        console.error('❌ Manual discovery exception:', error);
      }
    } else {
      console.log('❌ Cast API not available for manual discovery');
    }
  };

  const testChromecastWithoutDevices = () => {
    console.log('🧪 === CHROMECAST TEST MODE (NO DEVICES) ===');
    
    // Skip test entirely on iOS
    if (Capacitor.getPlatform() === 'ios') {
      console.log('🧪 iOS detected: Skipping Cast test');
      return;
    }
    
    // Test 1: Check API availability
    console.log('🧪 Test 1: API Availability');
    console.log('🧪 Window.chrome exists:', !!window.chrome);
    console.log('🧪 Window.chrome.cast exists:', !!window.chrome?.cast);
    console.log('🧪 Window.chrome.cast.isAvailable:', !!window.chrome?.cast?.isAvailable);
    
    if (!window.chrome?.cast?.isAvailable) {
      console.log('❌ Test 1 FAILED: Cast API not available');
      // Don't show toast message - this is just a test function
      return;
    }
    console.log('✅ Test 1 PASSED: Cast API is available');
    
    // Test 2: Simulate session request (will fail but we can catch the error)
    console.log('🧪 Test 2: Simulate Session Request');
    try {
      const timeoutId = setTimeout(() => {
        console.log('⏰ Test 2: Session request timeout (expected)');
        showToastMessage('Test completed: No devices found (expected)');
      }, 5000);
      
      window.chrome.cast.requestSession(
        (session: chrome.cast.Session) => {
          clearTimeout(timeoutId);
          console.log('✅ Test 2: Unexpected success! Found device:', session.receiver.friendlyName);
          showToastMessage(`Test completed: Found device ${session.receiver.friendlyName}`);
          session.stop(() => console.log('✅ Test session closed'), () => {});
        },
        (error: chrome.cast.Error) => {
          clearTimeout(timeoutId);
          console.log('✅ Test 2 PASSED: Expected error (no devices)');
          console.log('✅ Error code:', error.code);
          console.log('✅ Error description:', error.description);
          
          if (error.code === chrome.cast.ErrorCode.TIMEOUT || 
              error.code === chrome.cast.ErrorCode.RECEIVER_UNAVAILABLE ||
              error.code === chrome.cast.ErrorCode.SESSION_ERROR) {
            showToastMessage('Test completed: No devices found (working correctly)');
          } else {
            showToastMessage(`Test completed: Unexpected error code ${error.code}`);
          }
        }
      );
    } catch (error) {
      console.error('❌ Test 2 FAILED: Exception during session request:', error);
      showToastMessage('Test failed: Exception during session request');
    }
  };

  // Step 9: PWA-optimized Cast button handler with iOS AirPlay priority
  const handleCastButtonClick = async () => {
    console.log('🔘 Cast button clicked - Using PWA-optimized Cast hook');
    
    // Clear any previous errors
    clearError();
    
    // Step 9.5: PWA-specific Cast session handling
    const isStandalonePWA = window.matchMedia('(display-mode: standalone)').matches || 
                           window.navigator.standalone;
    const isIOS = Capacitor.getPlatform() === 'ios';
    
    if (isStandalonePWA) {
      console.log('🎬 PWA: Cast operation in standalone mode');
    }
    
    if (isIOS) {
      console.log('🎬 iOS: Using AirPlay-only mode');
    }
    
    // Stop casting if already active
    if (castState.isConnected) {
      try {
        await stopCastSession();
        trackCastingStop('chromecast');
        
        // Clear PWA session storage
        if (isStandalonePWA) {
          localStorage.removeItem('playfm_cast_session');
          console.log('🎬 PWA: Cleared Cast session from localStorage');
        }
        
        showToastMessage('Disconnected from Cast device');
      } catch (error) {
        console.error('Failed to stop Cast session:', error);
        showToastMessage('Failed to disconnect from Cast device');
      }
      return;
    }
    
    // iOS: Only use AirPlay
    if (isIOS) {
      if (audioRef.current && 'webkitShowPlaybackTargetPicker' in audioRef.current) {
        try {
          console.log('🎬 iOS: Showing AirPlay picker');
          audioRef.current.webkitShowPlaybackTargetPicker();
          return;
        } catch (error) {
          console.error('🎬 iOS: AirPlay failed:', error);
          showToastMessage('AirPlay not available');
          return;
        }
      } else {
        console.log('🎬 iOS: AirPlay not supported');
        showToastMessage('AirPlay not supported on this device');
        return;
      }
    }
    
    // Non-iOS: Try AirPlay first, then Chromecast
    if (audioRef.current && 'webkitShowPlaybackTargetPicker' in audioRef.current) {
      try {
        audioRef.current.webkitShowPlaybackTargetPicker();
        return;
      } catch (error) {
        console.log('AirPlay failed, trying Chromecast');
      }
    }
    
    // Start Cast session (only for non-iOS platforms)
    try {
      showToastMessage('Searching for Cast devices...');
      await startCastSession();
      
      // Load media if session was successful
      if (castState.isConnected) {
        await loadCastMedia({
          url: STREAM_URLS[currentQuality],
          title: currentTrack.title,
          artist: currentTrack.artist,
          imageUrl: currentTrack.artwork || '/logo.png'
        });
        
        // Pause local audio
        if (audioRef.current) {
          audioRef.current.pause();
        }
        
        // Save session state for PWA persistence
        if (isStandalonePWA && castState.receiverName) {
          localStorage.setItem('playfm_cast_session', JSON.stringify({
            receiverName: castState.receiverName,
            isConnected: true,
            timestamp: Date.now()
          }));
          console.log('🎬 PWA: Cast session saved to localStorage');
        }
        
        trackCastingStart('chromecast');
        showToastMessage(`Connected to ${castState.receiverName || 'Cast device'}!`);
      }
    } catch (error) {
      console.error('Cast session failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to connect to Cast device';
      showToastMessage(errorMessage);
    }
  };



  const updateCurrentShow = () => {
    if (!schedule) return;
    const todayIndex = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
    const dayData = schedule[todayIndex];
    if (!dayData) return;
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const foundShow = dayData.shows.find((slot: any) => {
      const [startTime, endTime] = slot.time.split(' - ');
      
      // Parse start time
      const startMatch = startTime.match(/(\d+)(?::(\d+))?(am|pm)/i);
      if (!startMatch) return false;
      let startHour = parseInt(startMatch[1]);
      const startMinute = startMatch[2] ? parseInt(startMatch[2]) : 0;
      const isStartPM = startMatch[3].toLowerCase() === 'pm';
      if (isStartPM && startHour !== 12) startHour += 12;
      if (!isStartPM && startHour === 12) startHour = 0;
      
      // Parse end time
      const endMatch = endTime.match(/(\d+)(?::(\d+))?(am|pm)/i);
      if (!endMatch) return false;
      let endHour = parseInt(endMatch[1]);
      const endMinute = endMatch[2] ? parseInt(endMatch[2]) : 0;
      const isEndPM = endMatch[3].toLowerCase() === 'pm';
      if (isEndPM && endHour !== 12) endHour += 12;
      if (!isEndPM && endHour === 12) endHour = 0;
      
      // Convert to minutes for comparison
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      const startTimeInMinutes = startHour * 60 + startMinute;
      const endTimeInMinutes = endHour * 60 + endMinute;
      
      // Handle overnight shows (e.g., 11pm - 2am)
      if (endTimeInMinutes < startTimeInMinutes) {
        // Show spans midnight, so check if current time is after start OR before end
        return currentTimeInMinutes >= startTimeInMinutes || currentTimeInMinutes < endTimeInMinutes;
      } else {
        // Normal case: check if current time is within range
        return currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes < endTimeInMinutes;
      }
    });
    setCurrentShow(foundShow || null);
    console.log('Current show for Home Now Playing:', foundShow);
    console.log('Current time:', new Date().toLocaleTimeString());
    if (foundShow) {
      console.log('Current show time:', foundShow.time);
    }
  };

  const updateMetadata = async () => {
    try {
      console.log('🔄 Starting metadata update...');
      const url = 'https://stream.playfmdublin.com/status-json.xsl';
      console.log('📡 Fetching metadata from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        mode: 'cors' // Explicitly set CORS mode
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      console.log('📄 Raw response text:', text.substring(0, 500) + '...');
      
      const contentType = response.headers.get('content-type');
      console.log('📄 Content type:', contentType);

      if (!contentType?.includes('application/json')) {
        console.error('❌ Invalid content type:', contentType);
        throw new Error('Invalid content type in response');
      }

      const data = JSON.parse(text);
      console.log('📊 Raw metadata data:', data);

      if (!data.icestats?.source) {
        console.error('❌ Invalid metadata structure:', data);
        throw new Error('Invalid metadata structure');
      }

      // Handle both array and single object source formats
      const source = Array.isArray(data.icestats.source) 
        ? data.icestats.source[0] 
        : data.icestats.source;

      if (!source) {
        console.error('❌ No source data found in metadata');
        throw new Error('No source data found');
      }

      console.log('🎵 Source data:', source);

      // Extract title and artist from the source data
      let title = source.title;
      let artist = source.artist;

      // If title contains both title and artist (common in Icecast), split them
      if (title && title.includes(' - ')) {
        [title, artist] = title.split(' - ').map((s: string) => s.trim());
      }

      const newMetadata = {
        title: title || 'Play FM',
        artist: artist || 'PlayFm Dublin',
        album: source.album || '24/7 From Ireland With Love',
        artwork: source.artwork || logo
      };

      console.log('🆕 New metadata:', newMetadata);
      console.log('📝 Current metadata:', currentTrack);

      // Only update if metadata has actually changed
      if (newMetadata.title !== currentTrack.title || newMetadata.artist !== currentTrack.artist) {
        console.log('✅ Metadata changed, updating...', newMetadata);
        setCurrentTrack(newMetadata);
        setLastUpdate(Date.now());
        setError(null);

        // Update media session if available and initialized
        if ('mediaSession' in navigator && mediaSessionInitializedRef.current) {
          try {
            navigator.mediaSession.metadata = new MediaMetadata({
              title: newMetadata.title,
              artist: newMetadata.artist,
              album: newMetadata.album,
              artwork: [
                { src: newMetadata.artwork || '/logo.png', sizes: '96x96', type: 'image/png' },
                { src: newMetadata.artwork || '/logo.png', sizes: '128x128', type: 'image/png' },
                { src: newMetadata.artwork || '/logo.png', sizes: '192x192', type: 'image/png' },
                { src: newMetadata.artwork || '/logo.png', sizes: '256x256', type: 'image/png' },
                { src: newMetadata.artwork || '/logo.png', sizes: '384x384', type: 'image/png' },
                { src: newMetadata.artwork || '/logo.png', sizes: '512x512', type: 'image/png' }
              ]
            });
            console.log('📱 Media session metadata updated');
          } catch (mediaError) {
            console.error('❌ Failed to update media session metadata:', mediaError);
          }
        }
      } else {
        console.log('⏭️ Metadata unchanged, skipping update');
      }

    } catch (error) {
      console.error('❌ Error updating metadata:', error);
      setError(error instanceof Error ? error.message : 'Failed to update metadata');
    }
  };

  // Force sync media session state
  const syncMediaSessionState = () => {
    if ('mediaSession' in navigator && mediaSessionInitializedRef.current) {
      try {
        if (isPlaying) {
          navigator.mediaSession.playbackState = 'playing';
        } else {
          navigator.mediaSession.playbackState = 'paused';
        }
        console.log('📱 Media session state synced:', navigator.mediaSession.playbackState);
      } catch (error) {
        console.error('❌ Failed to sync media session state:', error);
      }
    }
  };

  // Reset audio element to clean state
  const resetAudioElement = () => {
    if (audioRef.current) {
      const audio = audioRef.current;
      console.log('🔄 Resetting audio element...');
      
      // Pause and reset
      audio.pause();
      audio.currentTime = 0;
      
      // Clear any buffered data
      if (audio.buffered.length > 0) {
        console.log('🔄 Clearing audio buffer...');
      }
      
      // Reload the stream
      audio.load();
      
      // Reset state
      setIsPlaying(false);
      setIsLoading(false);
      setStreamError(false);
      
      // Sync media session
      syncMediaSessionState();
      
      console.log('🔄 Audio element reset complete');
    }
  };

  const toggleSchedule = () => {
    if (!showSchedule) {
      const today = new Date().getDay();
      const dayIndex = today === 0 ? 6 : today - 1;
      setInitialSlide(dayIndex);
    }
    setShowSchedule(!showSchedule);
  };

  const handleSleepTimerEnd = async () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    endSession();
  };




  const handleFavoriteToggle = (slot: { artist: string; show?: string; genre?: string }) => {
    const showId = `${slot.artist}-${slot.show || 'Show'}`;
    toggleFavoriteShow(showId);
  };

  // Calculate nextShow whenever currentShow or schedule changes
  useEffect(() => {
    if (!currentShow || !schedule) {
      setNextShow(null);
      return;
    }

    const now = new Date();
    const currentDayIndex = now.getDay() === 0 ? 6 : now.getDay() - 1;
    const currentDay = schedule[currentDayIndex];
    
    if (!currentDay) {
      setNextShow(null);
      return;
    }
    
    // Find the current show's index
    const currentShowIndex = currentDay.shows.findIndex((slot: any) =>
      slot.artist === currentShow.artist && slot.show === currentShow.show && slot.genre === currentShow.genre
    );

    if (currentShowIndex === -1) {
      setNextShow(null);
      return;
    }

    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinute;

    // First try to find the next show in the current day
    for (let i = currentShowIndex + 1; i < currentDay.shows.length; i++) {
      const slot = currentDay.shows[i];
      const [startTime] = slot.time.split(' - ');
      const startMatch = startTime.match(/(\d+)(?::(\d+))?(am|pm)/i);
      if (!startMatch) continue;
      
      let startHour = parseInt(startMatch[1]);
      const startMinute = startMatch[2] ? parseInt(startMatch[2]) : 0;
      const isStartPM = startMatch[3].toLowerCase() === 'pm';
      if (isStartPM && startHour !== 12) startHour += 12;
      if (!isStartPM && startHour === 12) startHour = 0;
      
      const startTimeInMinutes = startHour * 60 + startMinute;
      
      // Skip Play Show Replays unless it's the last show of the day
      if (slot.artist !== 'Play Show Replays' || i === currentDay.shows.length - 1) {
        if (startTimeInMinutes > currentTimeInMinutes) {
          setNextShow(slot);
          return;
        }
      }
    }

    // If no next show found in current day, look at the next day
    const nextDayIndex = (currentDayIndex + 1) % schedule.length;
    const nextDay = schedule[nextDayIndex];
    if (nextDay && nextDay.shows.length > 0) {
      // Find the first non-Play Show Replays show in the next day
      for (const slot of nextDay.shows) {
        if (slot.artist !== 'Play Show Replays') {
          setNextShow(slot);
          return;
        }
      }
      // If all shows are Play Show Replays, use the first one
      if (nextDay.shows.length > 0) {
        setNextShow(nextDay.shows[0]);
        return;
      }
    }

    setNextShow(null);
  }, [currentShow, schedule]);

  // Update the schedule subscription to use db directly
  useEffect(() => {
    try {
      const unsubscribe = onSnapshot(doc(db, 'schedule', 'main'), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // Check if the data has the expected structure
          if (data && data.data) {
            setSchedule(data.data);
          } else {
            console.warn('⚠️ Firebase data structure unexpected, using local fallback');
            setSchedule(scheduleData);
          }
        } else {
          console.warn('⚠️ No schedule found in Firestore, using local fallback');
          setSchedule(scheduleData);
        }
      }, (error) => {
        console.error('❌ Error in schedule subscription:', error);
        console.warn('⚠️ Using local schedule data as fallback');
        setSchedule(scheduleData);
      });
      return () => {
        unsubscribe();
      };
    } catch (error) {
      console.error('❌ Error setting up schedule subscription:', error);
      console.warn('⚠️ Using local schedule data as fallback');
      setSchedule(scheduleData);
    }
  }, []);

  // Recalculate currentShow whenever schedule changes or time passes


  // Add debug logging for next show
  useEffect(() => {
    if (nextShow) {
      console.log('Next show detected:', nextShow);
      console.log('Next show time:', nextShow.time);
    }
  }, [nextShow]);



  console.log('App component state initialized');

  // Add error boundary
  useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      console.error('Global error caught:', error);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Preload critical images for better performance
  useEffect(() => {
    const preloadImages = () => {
      const criticalImages = [
        logo,
        mixcloudIcon,
        '/public/images/logo.png'
      ];
      
      criticalImages.forEach(src => {
        const img = new Image();
        img.src = src;
      });
    };

    preloadImages();
  }, []);

  // Performance monitoring
  useEffect(() => {
    if ('performance' in window) {
      // Track app load time
      const loadTime = performance.now();
      console.log(`App loaded in ${loadTime.toFixed(2)}ms`);
      
      // Track user interactions
      const trackInteraction = () => {
        console.log('User interaction detected');
      };
      
      document.addEventListener('click', trackInteraction, { once: true });
      document.addEventListener('touchstart', trackInteraction, { once: true });
      
      return () => {
        document.removeEventListener('click', trackInteraction);
        document.removeEventListener('touchstart', trackInteraction);
      };
    }
  }, []);

  // Step 9: PWA-optimized Cast session management
  useEffect(() => {
    const handleAppStateChange = ({ isActive }: { isActive: boolean }) => {
      if (!isActive && castState.isConnected) {
        console.log('🎬 PWA: App going to background, maintaining Cast session');
        // Step 9.1: Maintain Cast sessions across app state changes
        // Store Cast session state in localStorage for PWA persistence
        if (castState.currentSession && castState.receiverName) {
          localStorage.setItem('playfm_cast_session', JSON.stringify({
            receiverName: castState.receiverName,
            isConnected: true,
            timestamp: Date.now()
          }));
          console.log('🎬 PWA: Cast session state saved to localStorage');
        }
        
        // Don't stop casting when app goes to background in PWA mode
        // The cast session should continue independently
      } else if (isActive && castState.isConnected) {
        console.log('🎬 PWA: App returning to foreground with active Cast session');
        // Step 9.2: Handle app foreground transitions
        // Verify Cast session is still active when app returns
        setTimeout(() => {
          if (castState.currentSession && window.chrome?.cast) {
            console.log('🎬 PWA: Verifying Cast session status after foreground transition');
            // The session should still be active, but we can add verification here if needed
          }
        }, 1000);
      }
    };

         // Step 9.3: PWA-specific event handling
     const handleVisibilityChange = () => {
       if (document.hidden && castState.isConnected) {
         console.log('🎬 PWA: App hidden with active Cast session');
         // Additional PWA-specific handling when app is hidden
       } else if (!document.hidden && castState.isConnected) {
         console.log('🎬 PWA: App visible with active Cast session');
         // Refresh Cast session status when app becomes visible
       }
     };

     // PWA visibility change handler (different from app state change)
     const handlePWAVisibilityChange = () => {
       const isActive = !document.hidden;
       handleAppStateChange({ isActive });
     };

     // Check if running in standalone PWA mode
     const isStandalonePWA = window.matchMedia('(display-mode: standalone)').matches || 
                            window.navigator.standalone;

     if (isStandalonePWA) {
       console.log('🎬 PWA: Running in standalone mode, optimizing Cast behavior');
       document.addEventListener('visibilitychange', handleVisibilityChange);
       document.addEventListener('visibilitychange', handlePWAVisibilityChange);
     }

     if (Capacitor.isNativePlatform()) {
       CapacitorApp.addListener('appStateChange', handleAppStateChange);
     }

     return () => {
       if (Capacitor.isNativePlatform()) {
         CapacitorApp.removeAllListeners();
       }
       if (isStandalonePWA) {
         document.removeEventListener('visibilitychange', handleVisibilityChange);
         document.removeEventListener('visibilitychange', handlePWAVisibilityChange);
       }
     };
  }, [castState.isConnected, castState.currentSession, castState.receiverName]);

  // Cleanup Chromecast sessions on component unmount
  useEffect(() => {
    return () => {
      // Clean up any active cast sessions when component unmounts
      if (isCasting && castSession) {
        console.log('🎬 Component unmounting, cleaning up cast session');
        if (Capacitor.getPlatform() === 'android') {
          Cast.stopCasting().catch(console.error);
        } else if (castSession && window.chrome?.cast) {
          castSession.stop(() => {
            console.log('🎬 Cast session stopped on unmount');
          }, (error: chrome.cast.Error) => {
            console.error('❌ Error stopping cast session on unmount:', error);
          });
        }
      }
    };
  }, [isCasting, castSession]);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col relative" data-testid="app-content">
      <div className="absolute inset-0 z-0">
        <DarkVeil hueShift={264} />
      </div>
      <audio
        ref={audioRef}
        preload="none"
        crossOrigin="anonymous"
      />
      <div className="relative z-10">
        <Routes>
        <Route path="/" element={
          <>


            {showInstallPrompt && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 w-full max-w-md text-center max-h-[90vh] overflow-y-auto">
                  <h3 className="text-xl font-semibold mb-2 text-white">Install PlayFm Dublin</h3>
                  <p className="text-white mb-4 text-shadow">Add to your home screen for the best experience</p>
                  
                  {/* iOS Safari-specific instructions */}
                  {browserType === 'safari' && (
                    <div className="mb-6 text-left">
                      <div className="bg-brand-pink/20 rounded-lg p-4 mb-4 border border-brand-pink/30">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-5 h-5 text-brand-pink" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                          <span className="text-brand-pink font-semibold">Important: Safari Only</span>
                        </div>
                        <p className="text-white/90 text-sm">The "Add to Home Screen" option is only available in Safari. If you're using Chrome or Firefox, please switch to Safari first.</p>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-brand-pink rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                          <div>
                            <p className="text-white font-medium">Tap the Share button on Safari</p>
                            <p className="text-white/70 text-sm">Look for the rectangle with arrow pointing up at the bottom of your screen</p>
                            <div className="mt-2 flex items-center gap-2 text-brand-pink">
                              <ArrowUpSquare className="w-4 h-4" />
                              <span className="text-xs">Share button</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-brand-pink rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                          <div>
                            <p className="text-white font-medium">Scroll down and tap "Add to Home Screen"</p>
                            <p className="text-white/70 text-sm">This option appears in the share menu</p>
                            <div className="mt-2 flex items-center gap-2 text-brand-pink">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                              </svg>
                              <span className="text-xs">Add to Home Screen</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-brand-pink rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                          <div>
                            <p className="text-white font-medium">Tap "Add" to confirm</p>
                            <p className="text-white/70 text-sm">The app will appear on your home screen</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Android instructions */}
                  {browserType === 'chrome' && (
                    <div className="mb-6 text-left">
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-brand-pink rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                          <div>
                            <p className="text-white font-medium">Tap the three dots menu (⋮)</p>
                            <p className="text-white/70 text-sm">Located at the bottom of your screen</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-brand-pink rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                          <div>
                            <p className="text-white font-medium">Tap "Install app" or "Add to Home screen"</p>
                            <p className="text-white/70 text-sm">Follow the installation prompts</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Generic instructions for other browsers */}
                  {!browserType || (browserType !== 'safari' && browserType !== 'chrome') && (
                    <div className="mb-6 text-left">
                      <div className="bg-yellow-500/20 rounded-lg p-4 mb-4 border border-yellow-500/30">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                          </svg>
                          <span className="text-yellow-500 font-semibold">Browser Notice</span>
                        </div>
                        <p className="text-white/90 text-sm">For the best experience, use Safari on iOS or Chrome on Android. Other browsers may have limited PWA support.</p>
                      </div>
                      
                      <p className="text-white/80 text-sm">Look for "Add to Home Screen" or "Install app" in your browser's menu.</p>
                    </div>
                  )}
                  
                  <div className="flex justify-center gap-4">
                    <button
                      onClick={() => setShowInstallPrompt(false)}
                      className="px-6 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors text-white border border-white/20"
                    >
                      Later
                    </button>
                    {browserType !== 'safari' &&
                      <button
                        onClick={handleInstallClick}
                        className="px-6 py-2 bg-brand-pink rounded-lg hover:bg-brand-pink/80 transition-colors text-white font-semibold"
                      >
                        Install
                      </button>
                    }
                  </div>
                </div>
              </div>
            )}

            <div className="container mx-auto px-4 pt-12 pb-8 flex-1 flex flex-col items-center justify-center pt-safe">
              {/* Place the logo at the top, above the tagline, and make it round */}
              <div className="flex flex-col items-center mt-8">
                <img src={logo} alt="Logo" className="w-24 h-24 mb-2 rounded-full object-cover" />
                <p className="text-white text-lg font-semibold mb-4">24/7 From Ireland With Love</p>
              </div>

              {/* Now Playing card */}
              {schedule ? (
                <>
                  {currentShow ? (
                    <div className="max-w-md w-full mx-auto bg-white/20 rounded-[2rem] p-6 border border-white/10 mb-6">
                      {/* Now playing info */}
                      <div className="flex flex-row items-center w-full">
                        <div className="w-20 h-20 rounded-full overflow-hidden ring-2 ring-white/20 flex-shrink-0">
                          <img
                            src={brokenImages[currentShow?.image] ? logo : currentShow?.image || logo}
                            alt={currentShow?.artist || 'PlayFm Dublin'}
                            className="w-full h-full object-cover"
                            onError={handleImageError}
                          />
                        </div>
                        <div className="flex flex-col justify-center ml-6 flex-1 min-w-0">
                          <div className="flex flex-col gap-2">
                            {currentShow.artist === 'Play Show Replays' && (
                              <span className="text-[0.85rem] font-semibold text-brand-pink">
                                Now Playing
                              </span>
                            )}
                            {currentShow.artist !== 'Play Show Replays' && (
                              <span className="flex items-center px-2 py-0.5 rounded-full border border-white text-[0.85rem] font-semibold text-brand-pink gap-1 self-start" style={{ borderWidth: '1px' }}>
                                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" style={{ display: 'inline-block' }}></span>
                                On Air
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 w-full">
                            <div className="truncate flex flex-col">
                              <span className="text-white font-bold text-base">{currentShow?.artist || 'PlayFm Dublin'}</span>
                              {currentShow?.show && (
                                <span className="text-white/80 text-base font-normal mt-1">{currentShow.show}</span>
                              )}
                            </div>
                          </div>
                          {currentShow?.genre && (
                            <div className="text-brand-pink text-sm font-medium truncate">{currentShow.genre}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="max-w-md w-full mx-auto bg-white/20 rounded-[2rem] p-6 border border-white/10 mb-6 text-center">
                      <p className="text-white">No show currently on air</p>
                      <p className="text-white/60 text-sm mt-2">Check the schedule for upcoming shows</p>
                    </div>
                  )}
                  {nextShow && (
                    <div className="max-w-md w-full mx-auto bg-white/20 rounded-[2rem] p-4 border border-white/10 mb-6">
                      <div className="flex flex-row items-center w-full">
                        <div className="w-20 h-20 rounded-full overflow-hidden ring-2 ring-white/20 flex-shrink-0">
                          <img
                            src={brokenImages[nextShow?.image] ? logo : nextShow?.image || logo}
                            alt={nextShow?.artist || 'PlayFm Dublin'}
                            className="w-full h-full object-cover"
                            onError={handleImageError}
                          />
                        </div>
                        <div className="flex flex-col justify-center ml-6 flex-1 min-w-0">
                          <div className="text-brand-pink text-xs font-bold leading-tight mb-0.5">
                            <span>Up next from:</span>
                            <span className="ml-2 text-white">{nextShow.time}</span>
                          </div>
                          <div className="text-white font-semibold text-base leading-tight truncate">{nextShow.artist}</div>
                          {nextShow?.show && (
                            <div className="text-white/80 text-sm font-normal truncate">{nextShow.show}</div>
                          )}
                          {nextShow?.genre && (
                            <div className="text-brand-pink text-xs font-medium mt-0.5 truncate">{nextShow.genre}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="max-w-md w-full mx-auto bg-white/20 rounded-[2rem] p-6 border border-white/10 mb-6 text-center">
                  <p className="text-white">Loading schedule...</p>
                </div>
              )}

              {/* Player controls card (restored above icons card) */}
              <div className="max-w-md w-full mx-auto bg-white/20 rounded-[2rem] p-6 border border-white/10 mb-6">
                <div className="flex justify-evenly w-full items-center gap-4">
                  <div className="relative">
                    <button
                      onClick={handlePlayPause}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-brand-pink text-white focus:outline-none focus:ring-2 focus:ring-pink-300 transition group"
                    >
                      {getIsPlaying() ? (
                        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="6" y="4" width="4" height="16" fill="white"/>
                          <rect x="14" y="4" width="4" height="16" fill="white"/>
                        </svg>
                      ) : (
                        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 5.14v14l11-7-11-7z" fill="white"/>
                        </svg>
                      )}
                    </button>

                  </div>
                  <button
                    onClick={handleCastButtonClick}
                    className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors ${
                      castState.isConnected 
                        ? 'bg-brand-pink text-white' 
                        : castState.isConnecting
                        ? 'bg-brand-pink/50 text-white'
                        : (Capacitor.getPlatform() === 'ios' && !isAirPlayAvailable) || (!isAirPlayAvailable && !castState.isAvailable)
                        ? 'text-white/30 cursor-not-allowed'
                        : 'text-white hover:bg-white/10'
                    }`}
                    title={
                      castState.isConnected 
                        ? `Stop Casting to ${castState.receiverName || 'Cast device'}` 
                        : castState.isConnecting
                        ? "Connecting..."
                        : Capacitor.getPlatform() === 'ios'
                        ? isAirPlayAvailable ? "AirPlay" : "AirPlay not available"
                        : isAirPlayAvailable 
                        ? "AirPlay" 
                        : castState.isAvailable 
                        ? "Cast" 
                        : "No casting devices available"
                    }
                    disabled={
                      (Capacitor.getPlatform() === 'ios' && !isAirPlayAvailable) || 
                      castState.isConnecting
                    }
                  >
                    <Airplay className="w-7 h-7" />
                  </button>
                  <button
                    onClick={toggleMute}
                    className="w-9 h-9 flex items-center justify-center rounded-full transition-colors"
                  >
                    {isMuted ? (
                      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" fill="currentColor"/>
                      </svg>
                    ) : (
                      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill="currentColor"/>
                      </svg>
                    )}
                  </button>
                  <button
                    onClick={handleShare}
                    className="w-9 h-9 flex items-center justify-center rounded-full transition-colors"
                  >
                    <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Icons card (restored below player controls card) */}
              <div className="max-w-md w-full mx-auto bg-white/20 rounded-[2rem] p-6 border border-white/10 mb-6">
                <div className="relative">
                  <Swiper
                    spaceBetween={16}
                    slidesPerView={4}
                    className="hide-scrollbar w-full"
                    onSwiper={(swiper) => {
                      swiperRef.current = swiper;
                    }}
                  >
                    {mainIcons.map((item, idx) => (
                      <SwiperSlide key={item.label} style={{ width: '72px', display: 'flex', justifyContent: 'center' }}>
                        <button
                          onClick={item.onClick}
                          className="w-12 h-12 flex flex-col items-center justify-center text-white/80 hover:text-brand-pink transition-colors rounded-full relative"
                          aria-label={item.aria}
                        >
                          <span className="flex items-center justify-center w-6 h-6">
                            {item.isCustom
                              ? item.icon({ className: 'w-6 h-6' })
                              : React.createElement(item.icon, { className: 'w-6 h-6' })
                            }
                          </span>
                          <span className="text-xs mt-1 leading-tight text-center w-full">{item.label}</span>
                          {item.badge && (
                            <span className="absolute -top-1 -right-1 bg-brand-pink text-white text-xs rounded-full px-2 py-0.5 font-bold animate-pulse">
                              {item.badge}
                            </span>
                          )}
                        </button>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  {/* Navigation dots */}
                  <div className="flex justify-center gap-2 mt-4">
                    {[0, 1].map((idx) => (
                      <button
                        key={idx}
                        className={`w-2 h-2 rounded-full transition-all ${
                          swiperRef.current?.activeIndex === idx ? 'bg-brand-pink scale-125' : 'bg-white/30 hover:bg-brand-pink/60'
                        }`}
                        onClick={() => swiperRef.current?.slideTo(idx)}
                        aria-label={`Go to page ${idx + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        } />
        <Route path="/schedule" element={<SchedulePage currentShow={currentShow} />} />
      </Routes>
      </div>

      <style>
        {styles}
      </style>
      
      {/* Toast Notification */}
      {showToast && toastMessage && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-black/80 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-lg max-w-sm text-center">
            <p className="text-sm font-medium">{toastMessage}</p>
          </div>
        </div>
      )}

      {/* Feature Components */}
      <SleepTimer
        isVisible={showSleepTimer}
        onClose={() => setShowSleepTimer(false)}
        onTimerEnd={handleSleepTimerEnd}
        isPlaying={getIsPlaying()}
      />


      <NotificationSettings
        isVisible={showNotificationSettings}
        onClose={() => setShowNotificationSettings(false)}
        scheduleData={scheduleData}
      />

      {authLoading && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-brand-pink border-t-transparent rounded-full animate-spin"></div>
            <div className="text-white text-lg font-semibold">Connecting to chat...</div>
          </div>
        </div>
      )}
      {showChat && authReady && (
        <Suspense fallback={<div>Loading chat...</div>}>
                      <ChatRoom onClose={() => {
              const now = Date.now();
              setLastSeenMessageTime(now);
              localStorage.setItem('lastSeenMessageTime', now.toString());
              setShowChat(false);
            }} />
        </Suspense>
      )}
    </div>
  );
}

export default App;

